import { Button, Screen, Text, TextInput } from "components"
import React, { useState, useCallback, useEffect } from "react"
import { Keyboard, StatusBar, TouchableOpacity, View } from "react-native"
import { Controller, useForm, FieldError } from "react-hook-form"
import { style } from "./styles"
import { translate } from "i18n"
import { useNavigation, useRoute } from "@react-navigation/native"
import { typography } from "theme"
import flash from "config/flash"
import { useColor } from "app/theme/getThemes"
import Icon from "@expo/vector-icons/Feather"
interface FormData {
  membershipNo: string
  phoneNumber: string
}
import { PhoneNumberInput } from "components/PhoneNumberInput"
import { useStores } from "models"

export const ExistingScreen = () => {
  const { companyTheme } = useColor()
  const route = useRoute()
  const routePhoneNumber = route?.params?.phoneNumber?.replace(/\s/g, '');
  const routeCountryCode = route?.params?.countryCode
  const routeCountryCca2 = route?.params?.countryCca2
  const { userStore } = useStores()
  const { flagInfo, setFlagInfo } = userStore
  const navigation = useNavigation<any>()
  const [isLoading, setIsLoading] = useState(false)
  const [initialDefaultCountry, setInitialDefaultCountry] = useState(routeCountryCca2 || flagInfo.cca2);
  const [countryCode, setCountryCode] = useState("");

  const {
    control,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    reValidateMode: "onSubmit",
    defaultValues: {
      membershipNo: "",
      phoneNumber: "",
    },
  })

  useEffect(() => {
    if (routePhoneNumber !== undefined) {
      reset({
        membershipNo: "",
        phoneNumber: routePhoneNumber,
      });
    }
  }, [routePhoneNumber, reset]);

  const membershipNo = watch("membershipNo")
  const phoneNumber = watch("phoneNumber")

  const onSubmit = async (data: FormData) => {
    const phoneNum = countryCode + data?.phoneNumber;
    const phoneNumLast = phoneNum.replace(/\s/g, '');

    const formattedPhone = phoneNumLast.startsWith("+")
      ? phoneNumLast?.slice(1)
      : phoneNumLast

    clearErrors()
    setIsLoading(true)

    const { membershipNo, phoneNumber } = data

    if (!membershipNo && !phoneNumber) {
      setError("membershipNo", {
        type: "manual",
        message: translate("errors.enterMembershipOrPhone"),
      })
      setError("phoneNumber", {
        type: "manual",
        message: translate("errors.enterMembershipOrPhone"),
      })
      setIsLoading(false)
      return
    }

    const uppercaseMembershipNo = membershipNo?.toUpperCase() || null

    const requestBody = {
      company_code: process.env.COMPANY_CODE,
      contact_number: formattedPhone || null,
      member_code: uppercaseMembershipNo || null,
    }

    try {
      const response = await fetch(`${process.env.API_URL}/api/public/member/info-get`, {
        method: "POST",
        body: JSON.stringify(requestBody),
        headers: {
          "Content-Type": "application/json",
        },
      })

      console.log("API response status:", response.status, response.statusText)

      if (!response.ok) {
        console.error("API request failed:", response.status, response.statusText)

        const errorData = await response.json().catch(() => null)
        console.error("Error data:", errorData)

        if (response.status === 404) {
          if (errorData && errorData.error === "Member not found") {
            flash("error", translate("errors.memberNotFound"))
          } else if (errorData && errorData.error === "This member has activated") {
            flash("error", "The member has already been activated")
          } else if (errorData && errorData.error === "MISSING_CONTACT") {
            flash("error", "Contact Number not found. Please reach out to the customer service")
          } else {
            flash("error", `Failed to call api`)
          }
        } else if (response.status === 400) {
          flash("error", "Request parameters error.")
        } else if (response.status >= 500) {
          console.log("Server Error")
          flash("error", "Internal Server Error")
        } else {
          console.log(`Status: Other Error status ${response.status}`)
          flash("error", `Failed to call API`)
        }
        setIsLoading(false)
        return
      }

      const apiResult = await response.json()
      console.log("Data from API:", apiResult)

      if (apiResult && apiResult.data && apiResult.data.length > 0) {
        console.log("Member Found and is inactive:", apiResult.data[0])
        setIsLoading(false)

        navigation.navigate("register", { userData: apiResult.data[0] })
      } else {
        console.error(
          "The API returned 200 OK, but the data format is incorrect or no data.",
          apiResult,
        )
        flash("error", apiResult?.message || "The API returned incorrect data.")
        setIsLoading(false)
      }
    } catch (error) {
      console.error("Error during API call or JSON parsing:", error)
      flash("error", "Network connection failed. Please check your network.")
      setIsLoading(false)
    }
  }

  return (
    <Screen onPress={Keyboard.dismiss}>
      <View style={style.container}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginTop: StatusBar.currentHeight + 10,
          }}
        >
          <TouchableOpacity
            style={{
              backgroundColor: companyTheme.first,
              borderRadius: 8,
              paddingHorizontal: 25,
              paddingVertical: 5,
            }}
            onPress={() => navigation.goBack()}
          >
            <Icon name="chevron-left" size={25} color="white" />
          </TouchableOpacity>
        </View>

        <View style={style.mv30}>
          <Text style={style.titleText}>{translate("existingScreen.welcomeBack")}</Text>
          <Text style={{ fontFamily: typography.primary.normal }}>
            {translate("existingScreen.description")}
          </Text>
        </View>

        <Controller
          name="membershipNo"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              onFocus={() => clearErrors("membershipNo")}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              editable={!phoneNumber}
              error={errors.membershipNo as FieldError | undefined}
              title={
                <Text>
                  {translate("existingScreen.memberShipNo")}
                </Text>
              }
              errorMessage={errors.membershipNo?.message}
              placeholder={translate("existingScreen.memberShipNoPlaceholder")}
              containerStyle={style.inputContainer}
              style={[style.inputStyle, !!phoneNumber && style.disabledInput]}
            />
          )}
        />

        <View style={style.orContainer}>
          <View style={style.line} />
          <Text style={style.orText}>{translate("existingScreen.or")}</Text>
          <View style={style.line} />
        </View>

        <Controller
          name="phoneNumber"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <PhoneNumberInput
                value={value || ""}
                defaultCountry={initialDefaultCountry}
                onChange={(formattedPhoneNumber) => {
                  onChange(formattedPhoneNumber);
                }}
                onCustomFocus={() => clearErrors('phoneNumber')}
                fieldError={error}
                errorMessage={error?.message}
                placeholder={translate("loginScreen.phoneNumberPlaceholder")}
                title={
                  <Text >
                    {translate("loginScreen.phoneNumber")}
                    <Text style={{ color: "red" }}></Text>
                  </Text>
                }
                containerStyle={style.inputContainer}
                inputStyle={style.inputStyle}
                onCountryChange={(country) => {
                  if (country) {
                    setCountryCode(country.callingCode);
                  }
                }}
              />
            )
          }}
        />

        <View style={style.buttonContainer}>
          <Button
            loading={isLoading}
            disabled={isLoading || (!membershipNo && !phoneNumber)}
            onPress={handleSubmit(onSubmit)}
            title={translate("existingScreen.submit")}
            style={isLoading || (!membershipNo && !phoneNumber) ? style.disabledButton : {}}
          />
        </View>
      </View>
    </Screen>
  )
}
